import { headers } from "./ApiHelpers";
import { ObjectPool } from "../../types/Objects";

const baseUrl = "/api/youtube";

const isErrorResponse = (response: unknown): response is { error: string } => {
  return typeof response === "object" && response !== null && "error" in response && typeof response.error === "string";
};

// Channels related contracts
type YoutubeChannelResponse = {
  channel: YoutubeChannel;
};

export type YoutubeChannel = {
  id: string;
  name: string;
};

export const getYoutubeChannel = async (url: string): Promise<YoutubeChannel> => {
  const params = new URLSearchParams();
  params.append("url", url);

  const raw = await fetch(`${baseUrl}/channel?${params.toString()}`, {
    method: "GET",
    headers: headers(),
  });
  const response: unknown = await raw.json();
  if (isYoutubeChannelResponse(response)) {
    return response.channel;
  }

  if (isErrorResponse(response)) {
    throw new Error(response.error);
  }

  throw new Error("Invalid YouTube channel response");
};

const isYoutubeChannelResponse = (response: unknown): response is YoutubeChannelResponse => {
  return (
    typeof response === "object" &&
    response !== null &&
    "channel" in response &&
    typeof response.channel === "object" &&
    response.channel !== null &&
    "id" in response.channel &&
    "name" in response.channel
  );
};

// Categories related contracts
type YoutubeCategoryResponse = {
  categories: YoutubeCategory[];
};

export type YoutubeCategory = {
  id: string;
  name: string;
};

export const getYoutubeCategories = async (): Promise<YoutubeCategory[]> => {
  const raw = await fetch(`${baseUrl}/categories`, {
    method: "GET",
    headers: headers(),
  });
  const response: unknown = await raw.json();
  if (isYoutubeCategoryResponse(response)) {
    return response.categories;
  }

  throw new Error("Invalid YouTube category response");
};

function isYoutubeCategoryResponse(response: unknown): response is YoutubeCategoryResponse {
  return (
    typeof response === "object" &&
    response !== null &&
    "categories" in response &&
    Array.isArray(response.categories) &&
    response.categories.every((category) => typeof category === "object" && "id" in category && "name" in category)
  );
}

// Videos related contracts
type YoutubeVideoResponse = {
  video: YoutubeVideo;
};

export type YoutubeVideo = {
  id: string;
  name: string;
  channel: YoutubeChannel;
  category: YoutubeCategory;
};

export const getYoutubeVideo = async (url: string): Promise<YoutubeVideo> => {
  const params = new URLSearchParams();
  params.append("url", url);

  const raw = await fetch(`${baseUrl}/video?${params.toString()}`, {
    method: "GET",
    headers: headers(),
  });
  const response: unknown = await raw.json();
  if (isYoutubeVideoResponse(response)) {
    return response.video;
  }

  if (isErrorResponse(response)) {
    throw new Error(response.error);
  }

  throw new Error("Invalid YouTube video response");
};

const isYoutubeVideoResponse = (response: unknown): response is YoutubeVideoResponse => {
  return (
    typeof response === "object" &&
    response !== null &&
    "video" in response &&
    typeof response.video === "object" &&
    response.video !== null &&
    "id" in response.video &&
    "name" in response.video
  );
};

export type YoutubeBulkImportRequest = {
  objectPool: ObjectPool;
  videos: string[];
  channels: string[];
  categories: string[];
};

/**
 * Bulk import YouTube entries into an object pool
 * @param payload
 * @returns boolean indicating if the request was accepted
 */
export const youtubeBulkImport = async (payload: YoutubeBulkImportRequest): Promise<boolean> => {
  const endpoint = `${baseUrl}/import`;
  const raw = await fetch(endpoint, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(payload),
  });
  return raw.status === 202;
};
